'use client'

import React from 'react'
import type { ProcessedTheme, ProcessedEntry, DomainDetail } from './processedTypes'
import { watchClasses, entryHostname, campaignCode } from './processedTypes'
import { Widget } from '../../Widget'


function WatchField({ label, value, theme, accent }: {
  label: string
  value: React.ReactNode
  theme: ProcessedTheme
  accent?: 'red' | 'green'
}) {
  const c = watchClasses(theme)
  const valueClass = accent === 'red' ? 'text-destructive' : accent === 'green' ? 'text-success' : c.text
  return (
    <div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${c.label}`}>{label}</div>
      <div className={`mt-1 break-words font-mono text-[16px] leading-snug ${valueClass}`}>{value}</div>
    </div>
  )
}

export function DomainIntelligencePanel({
  entry,
  domain,
  theme,
}: {
  entry: ProcessedEntry
  domain: DomainDetail | null
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const domainMeta = domain?.domainMetadata ?? {}
  const domainInfo = domain?.domainInfo ?? {}
  const ip = domainMeta.ip_address ?? domainInfo.ip
  const asn = domainMeta.asn ?? domainInfo.asn
  const registrar = domainInfo.registrar ?? domainMeta.registrar ?? '—'
  const created = domainMeta.create_date ?? domainInfo.created ?? domain?.createdAt ?? entry.createdAt
  const hostname = entryHostname(entry)
  const statusText = domain?.manualInactive ? 'Manual off' : domainMeta.is_active === true ? 'Active' : domainMeta.is_active === false ? 'Down' : 'Unknown'

  const statusIndicator = (
    <span className={`inline-flex items-center gap-1 text-xs ${domainMeta.is_active === true ? 'text-success' : c.faint}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${domainMeta.is_active === true ? 'bg-success' : 'bg-subtle'}`} />
      {domainMeta.is_active === true ? 'Live' : 'Down'}
    </span>
  )

  return (
    <Widget label="Domain intelligence" theme={theme} headerRight={statusIndicator}>
      <div className="font-mono text-lg mb-6">{hostname ?? domain?.originalUrl ?? '—'}</div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
        <WatchField label="Registrar" value={registrar} theme={theme} />
        <WatchField label="Created" value={created} theme={theme} />
        <WatchField label="IP address" value={ip ?? '—'} theme={theme} accent={ip ? 'red' : undefined} />
        <WatchField label="ASN" value={asn ?? '—'} theme={theme} />
        <WatchField label="Campaign" value={campaignCode(entry, domain)} theme={theme} />
        <WatchField label="Status" value={statusText} theme={theme} accent={statusText === 'Active' ? 'red' : undefined} />
      </div>
    </Widget>
  )
}
